/*
 * How a pregnancy reads in the sidebar list (KIN-2009).
 *
 * Every entry used to render identically — a heart, the word "Pregnancy" and a date — so an open
 * pregnancy and one completed last year were indistinguishable. For a woman with several on file,
 * which is the ordinary case rather than an edge one, the only way to find the live one was to
 * open each in turn.
 *
 * The completion fields have been on the object since KIN-1940; nothing here needs the backend.
 */
import { formatDate } from "utils/dateFormatter.js";

const hasText = (value) => typeof value === "string" && !!value.trim();

export const isPregnancyCompleted = (pregnancy) =>
  pregnancy?.completed === true;

/**
 * What the entry says under "Pregnancy".
 *
 * A completed pregnancy is dated by when it ended, not by a due date it never reached — "Completed
 * 14 Mar 2026" says what happened, where a bare estimated due date in the past invites the reader
 * to work it out. It falls back to the due date when the backend has `completed` without a date,
 * rather than showing a label with nothing after it.
 */
export const describePregnancyEntry = (pregnancy) => {
  const completed = isPregnancyCompleted(pregnancy);
  const startedDate = pregnancy?.estimatedDueDate || pregnancy?.lastMenstrualPeriod;

  if (!completed) {
    return {
      isCompleted: false,
      dateLabel: startedDate ? formatDate(startedDate) : null,
      reason: null,
    };
  }

  const endedDate = pregnancy?.completedAt || startedDate;

  return {
    isCompleted: true,
    dateLabel: endedDate ? `Completed ${formatDate(endedDate)}` : "Completed",
    // Free text, and usually the thing a GP most wants to know — "Miscarriage" carries weight
    // that should not need a click to find.
    reason: hasText(pregnancy?.completionReason)
      ? pregnancy.completionReason.trim()
      : null,
  };
};

const lastMenstrualPeriodTime = (pregnancy) => {
  const time = new Date(pregnancy?.lastMenstrualPeriod).getTime();

  // A missing or unparseable date sorts last rather than throwing the whole list into NaN order.
  return Number.isNaN(time) ? -Infinity : time;
};

/**
 * Open pregnancies first, each group newest first.
 *
 * Sorting purely by last menstrual period, as before, buries the live pregnancy under more recent
 * history whenever one was entered retrospectively.
 */
export const sortPregnanciesForSidebar = (pregnancies) =>
  [...(pregnancies ?? [])].sort((left, right) => {
    const leftCompleted = isPregnancyCompleted(left);
    const rightCompleted = isPregnancyCompleted(right);

    if (leftCompleted !== rightCompleted) return leftCompleted ? 1 : -1;

    return lastMenstrualPeriodTime(right) - lastMenstrualPeriodTime(left);
  });
